import { Bot, Check, Search, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useOrganization } from '../../contexts/OrganizationContext';
import { AgentService } from '../../services';
import { Agent } from '../../types/entities';
import { Badge, Button, Input, Modal } from './';

interface AgentSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectAgents: (agents: Agent[]) => void;
  selectedAgentIds?: number[];
}

const AgentSelector: React.FC<AgentSelectorProps> = ({
  isOpen,
  onClose,
  onSelectAgents,
  selectedAgentIds = [],
}) => {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAgents, setSelectedAgents] = useState<Agent[]>([]);

  const { currentOrganization } = useOrganization();
  const agentService = new AgentService();

  useEffect(() => {
    if (isOpen) {
      loadAgents();
    }
  }, [isOpen, currentOrganization]);

  useEffect(() => {
    // Carregar agentes pré-selecionados
    if (selectedAgentIds.length > 0 && agents.length > 0) {
      const preSelected = agents.filter(agent =>
        selectedAgentIds.includes(agent.id)
      );
      setSelectedAgents(preSelected);
    }
  }, [selectedAgentIds, agents]);

  const loadAgents = async () => {
    if (!currentOrganization) return;

    try {
      setIsLoading(true);
      const agentsData = await agentService.getActiveByOrganization(
        currentOrganization.id
      );
      setAgents(agentsData);
    } catch (error) {
      console.error('Erro ao carregar agentes:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredAgents = agents.filter(
    agent =>
      (agent.name?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
      (agent.role?.toLowerCase() || '').includes(searchQuery.toLowerCase())
  );

  const handleToggleAgent = (agent: Agent) => {
    setSelectedAgents(prev => {
      const isSelected = prev.some(selected => selected.id === agent.id);
      if (isSelected) {
        return prev.filter(selected => selected.id !== agent.id);
      } else {
        return [...prev, agent];
      }
    });
  };

  const handleConfirmSelection = () => {
    onSelectAgents(selectedAgents);
    onClose();
  };

  const handleClearSelection = () => {
    setSelectedAgents([]);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Selecionar Agentes"
      size="lg"
    >
      <div className="space-y-4">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Buscar agentes..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Selected Agents Summary */}
        {selectedAgents.length > 0 && (
          <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-blue-900 dark:text-blue-300">
                Agentes Selecionados ({selectedAgents.length})
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={handleClearSelection}
                className="text-blue-600 hover:text-blue-700"
              >
                <X className="h-3 w-3 mr-1" />
                Limpar
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {selectedAgents.map(agent => (
                <Badge key={agent.id} variant="secondary" className="text-xs">
                  {agent.name}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Agents List */}
        <div className="max-h-96 overflow-y-auto space-y-2">
          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500 mx-auto mb-2"></div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Carregando agentes...
              </p>
            </div>
          ) : filteredAgents.length === 0 ? (
            <div className="text-center py-8">
              <Bot className="h-8 w-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {searchQuery
                  ? 'Nenhum agente encontrado'
                  : 'Nenhum agente disponível'}
              </p>
            </div>
          ) : (
            filteredAgents.map(agent => {
              const isSelected = selectedAgents.some(
                selected => selected.id === agent.id
              );

              return (
                <div
                  key={agent.id}
                  className={`p-3 border rounded-lg cursor-pointer transition-all ${
                    isSelected
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                  onClick={() => handleToggleAgent(agent)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div
                        className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                          isSelected
                            ? 'border-primary-500 bg-primary-500'
                            : 'border-gray-300 dark:border-gray-600'
                        }`}
                      >
                        {isSelected && <Check className="h-3 w-3 text-white" />}
                      </div>
                      <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                        <Bot className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          {agent.name}
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {agent.role || 'Sem função definida'}
                        </p>
                      </div>
                    </div>
                    <Badge
                      variant={agent.is_active ? 'success' : 'secondary'}
                      size="sm"
                    >
                      {agent.is_active ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </div>

                  {agent.goal && (
                    <div className="mt-2 ml-7">
                      <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2">
                        <span className="font-medium">Objetivo:</span>{' '}
                        {agent.goal}
                      </p>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-2 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmSelection}
            disabled={selectedAgents.length === 0}
          >
            Selecionar{' '}
            {selectedAgents.length > 0 && `(${selectedAgents.length})`}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AgentSelector;
